import React, { useState, useEffect } from 'react'
import LoginHeader from '../../components/header/LoginHeaderA'
import './MiniAgentReferralHistory.css';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../src/constant.js';

function AgentReferralHistory() {
    const [currentPage, setCurrentPage] = useState(1);
    const [agentReferrals, setAgentReferrals] = useState([]);
    const [businessReferrals, setBusinessReferrals] = useState([]);
    const [loading, setLoading] = useState({ agents: true, businesses: true });
    const [error, setError] = useState({ agents: '', businesses: '' });
    const [activeTab, setActiveTab] = useState('agents'); // 'agents' or 'businesses'
    const [agentTotalPages, setAgentTotalPages] = useState(1);
    const [businessTotalPages, setBusinessTotalPages] = useState(1);
    const [agentSummary, setAgentSummary] = useState({
        total_referrals: 0,
        verified_agents: 0,
        pending_verification: 0
    });
    const [businessSummary, setBusinessSummary] = useState({
        total_referrals: 0,
        approved_businesses: 0,
        pending_approval: 0
    });
    const navigate = useNavigate();

    // Fetch agent referral data
    const fetchAgentReferralHistory = async (page = 1) => {
        try {
            setLoading(prev => ({ ...prev, agents: true }));
            const token = localStorage.getItem('auth_token');
            
            const response = await fetch(
                `${API_URL}/api/agent/referrals/agents?page=${page}&per_page=6`, 
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch agent referral history');
            }

            const data = await response.json();
            
            if (data.success) {
                setAgentReferrals(data.data.referred_agents || []);
                setAgentSummary({
                    total_referrals: data.data.summary?.total_referred_agents || 0,
                    verified_agents: data.data.summary?.verified_agents || 0,
                    pending_verification: data.data.summary?.pending_verification || 0
                });
                setAgentTotalPages(data.data.pagination?.last_page || 1);
                setError(prev => ({ ...prev, agents: '' }));
            } else {
                throw new Error(data.message || 'Failed to load agent referral history');
            }
            
        } catch (err) {
            setError(prev => ({ ...prev, agents: err.message }));
            console.error('Error fetching agent referral history:', err);
        } finally {
            setLoading(prev => ({ ...prev, agents: false }));
        }
    };

    // Fetch business referral data
    const fetchBusinessReferralHistory = async (page = 1) => {
        try {
            setLoading(prev => ({ ...prev, businesses: true }));
            const token = localStorage.getItem('auth_token');
            
            const response = await fetch(
                `${API_URL}/api/agent/referrals/businesses?page=${page}&per_page=6`, 
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch business referral history');
            }

            const data = await response.json();
            
            if (data.success) {
                setBusinessReferrals(data.data.referred_businesses || []);
                setBusinessSummary({
                    total_referrals: data.data.summary?.total_referred_businesses || 0,
                    approved_businesses: data.data.summary?.approved_businesses || 0,
                    pending_approval: data.data.summary?.pending_approval || 0
                });
                setBusinessTotalPages(data.data.pagination?.last_page || 1);
                setError(prev => ({ ...prev, businesses: '' }));
            } else {
                throw new Error(data.message || 'Failed to load business referral history');
            }
            
        } catch (err) {
            setError(prev => ({ ...prev, businesses: err.message }));
            console.error('Error fetching business referral history:', err);
        } finally {
            setLoading(prev => ({ ...prev, businesses: false }));
        }
    };

    // Fetch both agent and business data on component mount
    useEffect(() => {
        fetchAgentReferralHistory(1);
        fetchBusinessReferralHistory(1);
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        if (activeTab === 'agents') {
            fetchAgentReferralHistory(newPage);
        } else {
            fetchBusinessReferralHistory(newPage);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1); // Reset to first page when switching tabs
    };

    // Get current referrals based on active tab
    const getCurrentReferrals = () => {
        return activeTab === 'agents' ? agentReferrals : businessReferrals;
    };

    // Get current summary based on active tab
    const getCurrentSummary = () => {
        return activeTab === 'agents' ? agentSummary : businessSummary;
    };

    // Get total pages based on active tab
    const getTotalPages = () => {
        return activeTab === 'agents' ? agentTotalPages : businessTotalPages;
    };

    // Get current loading state
    const getCurrentLoading = () => {
        return activeTab === 'agents' ? loading.agents : loading.businesses;
    };

    // Get current error state
    const getCurrentError = () => {
        return activeTab === 'agents' ? error.agents : error.businesses;
    };

    // Helper function to format location display
    const formatLocation = (referral) => {
        if (referral.country && referral.region) {
            return `${referral.country}, ${referral.region}`;
        } else if (referral.country) {
            return referral.country;
        } else if (referral.region) {
            return referral.region;
        }
        return 'N/A';
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Get status text
    const getStatusText = (status) => {
        if (activeTab === 'agents') {
            return status === 'verified' ? 'Active' : (status || 'Pending');
        } else {
            return status === 'approved' ? 'Active' : (status || 'Pending');
        }
    };

    // Get status class
    const getStatusClass = (status) => {
        if (activeTab === 'agents') {
            return status === 'verified' ? 'active' : 'pending';
        } else {
            return status === 'approved' ? 'active' : 'pending';
        }
    };

    // Loading icon component
    const LoadingIcon = () => (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '10px', color: 'white' }}>
                Loading {activeTab === 'agents' ? 'agent' : 'business'} referral history...
            </p>
        </div>
    );

    const handleRetry = () => {
        if (activeTab === 'agents') {
            fetchAgentReferralHistory(currentPage);
        } else {
            fetchBusinessReferralHistory(currentPage);
        }
    };

    return (
        <div className="pt-5" style={{ backgroundColor: "#001964", minHeight: "100vh" }}>
            <LoginHeader />
            <div className="wallet-container" style={{minHeight:"600px"}}>
                <div className="referral-table">
                    <div className="table-header">
                        <h2>Referral History</h2>
                        <button className="close-btn" style={{marginTop: "-20px", marginLeft: "900px"}} onClick={() => navigate(-1)}>✕</button>
                    </div>
                    
                    {/* Tab Navigation */}
                    <div className="tab-navigation">
                        <div className="tab-buttons">
                            <button 
                                className={`tab-button ${activeTab === 'agents' ? 'active' : ''}`}
                                onClick={() => handleTabChange('agents')}
                            >
                                <span className="tab-label">Referred Agents</span>
                                {agentSummary.total_referrals > 0 && (
                                    <span className="tab-count">{agentSummary.total_referrals}</span>
                                )}
                            </button>
                            <button 
                                className={`tab-button ${activeTab === 'businesses' ? 'active' : ''}`}
                                onClick={() => handleTabChange('businesses')}
                            >
                                <span className="tab-label">Referred Businesses</span>
                                {businessSummary.total_referrals > 0 && (
                                    <span className="tab-count">{businessSummary.total_referrals}</span>
                                )}
                            </button>
                        </div>
                        
                        {/* Tab Content Indicator */}
                        <div className="tab-indicator">
                            <div 
                                className={`indicator-line ${activeTab === 'agents' ? 'left' : 'right'}`}
                            ></div>
                        </div>
                    </div>

    

                    {/* Table Content */}
                    <div className="table-content">
                        {getCurrentLoading() ? (
                            <LoadingIcon />
                        ) : getCurrentError() ? (
                            <div className="error-container">
                                <div className="error-icon">⚠️</div>
                                <h3>Error Loading Data</h3>
                                <p>{getCurrentError()}</p>
                                <button className="retry-btn" onClick={handleRetry}>
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="user-table">
                                        <thead>
                                            <tr>
                                                <th>No</th>
                                                <th>{activeTab === 'agents' ? 'Agent Name' : 'Business Name'}</th>
                                                <th>{activeTab === 'agents' ? 'Email' : 'Email'}</th>
                                                <th>Location</th>
                                                <th>Status</th>
                                                <th>Joined Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getCurrentReferrals().length > 0 ? (
                                                getCurrentReferrals().map((referral, index) => (
                                                    <tr key={referral.id || index}>
                                                        <td>{(currentPage - 1) * 6 + index + 1}</td>
                                                        <td className="name-cell">
                                                            {activeTab === 'agents' 
                                                                ? referral.name || `${referral.first_name || ''} ${referral.last_name || ''}`.trim()
                                                                : referral.business_name || referral.name || 'N/A'
                                                            }
                                                        </td>
                                                        <td>
                                                            {activeTab === 'agents' 
                                                                ? referral.email || 'N/A'
                                                                : referral.email|| 'Retail Business'
                                                            }
                                                        </td>
                                                        <td>{formatLocation(referral)}</td>
                                                        <td>
                                                            <span className={`status-badge ${getStatusClass(referral.status)}`}>
                                                                {getStatusText(referral.status)}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(referral.join_date || referral.registration_date || referral.created_at)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6">
                                                        <div className="empty-state">
                                                            <div className="empty-icon">
                                                                {activeTab === 'agents' ? '👤' : '🏢'}
                                                            </div>
                                                            <h3>No {activeTab === 'agents' ? 'Agents' : 'Businesses'} Found</h3>
                                                            <p>
                                                                You haven't referred any {activeTab === 'agents' ? 'agents' : 'businesses'} yet.
                                                            </p>
                                                            <button 
                                                                className="start-referring-btn"
                                                                onClick={() => navigate('/retail-agent/wallet')}
                                                            >
                                                                Start Referring
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {getTotalPages() > 1 && getCurrentReferrals().length > 0 && (
                                    <div className="pagination-container">
                                        <div className="pagination">
                                            <button 
                                                className="page-btn prev-next" 
                                                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                ← Previous
                                            </button>
                                            
                                            <div className="page-numbers">
                                                {Array.from({ length: Math.min(getTotalPages(), 5) }, (_, i) => {
                                                    let pageNum;
                                                    if (getTotalPages() <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= getTotalPages() - 2) {
                                                        pageNum = getTotalPages() - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <button
                                                            key={i}
                                                            className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            
                                            <button 
                                                className="page-btn prev-next" 
                                                onClick={() => handlePageChange(Math.min(currentPage + 1, getTotalPages()))}
                                                disabled={currentPage === getTotalPages()}
                                            >
                                                Next →
                                            </button>
                                        </div>
                                        
                                        <div className="page-info">
                                            Page {currentPage} of {getTotalPages()}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AgentReferralHistory;